// The power savers (D294): five ways the client stops spending compute on things nobody can
// see, each one a separate switch.
//
// They are per GRAPHICS TIER (`DetailSpec.savers` in detail.ts), so a tier can keep or drop
// any of them without touching the code that implements it, and every one can be turned off
// from the URL for an A/B measurement:
//
//     ?savers=0                         all off (the pre-D294 behaviour, exactly)
//     ?savers=1                         all on, whatever the tier says
//     ?savers=-idleSlowdown,-calmWater  the tier's set, minus these
//     ?savers=+cssOffscreen             the tier's set, plus this
//
// PURE: no three.js, no DOM. The stage, the ocean, the pacer and main.ts read the resolved set.

export interface PowerSavers {
  /**
   * While a modal or the pause menu dims the board, its BACKGROUND motion stops (the sea, the
   * flock, a pulsing glow). A real change — a rival's build, a flight — still draws, so the
   * board behind the scrim is never stale; it is just no longer animated for nobody.
   */
  pauseUnderModal: boolean;
  /** After a stretch with no player input, the background motion steps down its frame rate. */
  idleSlowdown: boolean;
  /**
   * The planar reflection is re-captured when something on the board changed (or the camera
   * moved), plus a slow refresh for the sky's drift — not on every other render.
   */
  reflectionOnChange: boolean;
  /** The sea disc carries no rings inside the calm band, where the shader holds it flat. */
  calmWater: boolean;
  /**
   * Endless CSS animations pause where nobody can see them: scrolled off-screen on the landing
   * page, and behind a modal's scrim in a game.
   */
  cssOffscreen: boolean;
}

export type PowerSaverKey = keyof PowerSavers;

export const POWER_SAVER_KEYS: readonly PowerSaverKey[] = Object.freeze(['pauseUnderModal', 'idleSlowdown', 'reflectionOnChange', 'calmWater', 'cssOffscreen'] as const);

export const ALL_SAVERS: Readonly<PowerSavers> = Object.freeze({
  pauseUnderModal: true,
  idleSlowdown: true,
  reflectionOnChange: true,
  calmWater: true,
  cssOffscreen: true,
});

export const NO_SAVERS: Readonly<PowerSavers> = Object.freeze({
  pauseUnderModal: false,
  idleSlowdown: false,
  reflectionOnChange: false,
  calmWater: false,
  cssOffscreen: false,
});

/** A URL override: replace the whole set, or adjust individual switches. */
export type SaverOverride = { all?: boolean; set?: Partial<PowerSavers> };

const isKey = (k: string): k is PowerSaverKey => (POWER_SAVER_KEYS as readonly string[]).includes(k);

/**
 * Read `?savers=` off a query string. Unknown names are ignored rather than thrown — a typo
 * should give you the game, not a blank page (the renderSettingsFromQuery rule).
 */
export function parseSaverOverride(search: string): SaverOverride {
  const raw = new URLSearchParams(search).get('savers');
  if (raw === null) return {};
  const v = raw.trim();
  if (v === '0' || v === 'false' || v === 'off') return { all: false };
  if (v === '1' || v === 'true' || v === 'on') return { all: true };
  const set: Partial<PowerSavers> = {};
  for (const part of v.split(',')) {
    const p = part.trim();
    const on = !p.startsWith('-');
    const name = p.replace(/^[-+]/, '');
    if (isKey(name)) set[name] = on;
  }
  return { set };
}

/** The tier's savers with an override applied. */
export function applySaverOverride(base: Readonly<PowerSavers>, o: SaverOverride): PowerSavers {
  const start = o.all === undefined ? base : o.all ? ALL_SAVERS : NO_SAVERS;
  return { ...start, ...(o.set ?? {}) };
}
